// VX-4302: pure coordination policy for the worker-generation escape hatch — the top rung of the
// recovery ladder. No Recoil/React dependencies; the atom and useOpenPixelDataSharedWorker wrap
// these decisions so the ladder/quorum/cooldown logic is unit-testable in isolation. Guiding
// principle: the most disruptive action carries the most conservative trigger, so every default
// here errs toward NOT bumping.

import { clamp } from 'utils/math';

export interface GenerationState {
  // Current SharedWorker generation (0 = the bare-named worker; see workerGeneration.ts).
  generation: number;
  // Epoch ms of the last committed bump; 0 = never bumped this lineage.
  lastBumpAt: number;
  // Number of recent consecutive bumps; drives the cooldown and resets after a sustained-delivery
  // dwell (handled by the caller, not here).
  backoffLevel: number;
}

// Number of consecutive connection-recreates that must each fail to restore delivery before a
// single observer considers its ladder exhausted. Conservative default (the spec leaves 2 vs 3
// open); ~3 dead-delivery cycles sit in the empirical no-man's-land between healthy (<122 s) and a
// real wedge (24 min–3.3 h).
export const BUMP_AFTER_FAILED_RECREATES = 3;

// After this many escalating bumps without sustained recovery, the fault is almost certainly
// environmental — surface the ambient "imaging delivery interrupted" indicator (Attention ladder).
export const BACKOFF_CEILING = 2;

// Cooldown schedule between automatic bumps: 30s → 1m → 2m, capped. Kept short because a radiologist
// will not wait minutes for images — the cooldown only needs to bound the blast radius of the one
// false positive the ladder can't catch (a shared network/backend outage), not pace normal recovery.
export const BASE_COOLDOWN_MS = 30_000;
export const MAX_COOLDOWN_MS = 2 * 60_000;

// Quorum protects OTHER clients from one confused client; a solo eligible observer has none to
// disrupt, so it may bump alone.
const QUORUM_CAP = 2;

export function quorumThreshold(eligibleCount: number): number {
  return clamp(eligibleCount, 0, QUORUM_CAP);
}

// Level 0 (first bump of an incident) is cooldown-free — only quorum-gated. From level 1 the
// cooldown doubles each step until the cap.
export function cooldownMs(backoffLevel: number): number {
  if (backoffLevel <= 0) return 0;
  return Math.min(BASE_COOLDOWN_MS * 2 ** (backoffLevel - 1), MAX_COOLDOWN_MS);
}

export function canBump(stateValue: GenerationState, now: number): boolean {
  return now - stateValue.lastBumpAt >= cooldownMs(stateValue.backoffLevel);
}

export function isLadderExhausted(failedRecreateCount: number): boolean {
  return failedRecreateCount >= BUMP_AFTER_FAILED_RECREATES;
}

export interface ShouldBumpInput {
  eligibleCount: number;
  exhaustedVotes: number;
  state: GenerationState;
  now: number;
}

export function shouldBump({
  eligibleCount,
  exhaustedVotes,
  state,
  now,
}: ShouldBumpInput): boolean {
  if (eligibleCount < 1) return false;
  if (exhaustedVotes < quorumThreshold(eligibleCount)) return false;
  return canBump(state, now);
}

export function proposeBump(stateValue: GenerationState, now: number): GenerationState {
  return {
    generation: stateValue.generation + 1,
    lastBumpAt: now,
    backoffLevel: stateValue.backoffLevel + 1,
  };
}

// Flag-gated read: a flag-off client ignores the atom entirely (bare name, generation 0) even if
// localStorage holds a stale generation from an earlier flag-on session.
export function selectGeneration(flagEnabled: boolean, state: GenerationState): number {
  return flagEnabled ? state.generation : 0;
}

// Cross-tab merge: adopt a strictly newer generation, otherwise keep local. Two tabs proposing the
// same next generation off the same pre-bump generation both converge here (gen-4, not gen-5), and
// re-receiving an already-applied commit is a no-op — so the bump is idempotent.
export function adoptGeneration(
  local: GenerationState,
  incoming: GenerationState
): GenerationState {
  return incoming.generation > local.generation ? incoming : local;
}
